import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Mail, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../App';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paid = searchParams.get('paid');
  const { clearCart } = useCart();
  const [status, setStatus] = useState(paid === 'true' ? 'success' : 'checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    clearCart();
  }, [clearCart]);

  const checkStatus = useCallback(async () => {
    if (!sessionId || attempts >= 10) {
      if (attempts >= 10) setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        const orderValue = res.data.amount_total ? res.data.amount_total / 100 : 0;
        const orderCurrency = (res.data.currency || 'pln').toUpperCase();
        if (window.gtag) window.gtag('event', 'purchase', {
          transaction_id: sessionId,
          value: orderValue,
          currency: orderCurrency
        });
        // Meta Pixel — fires only if the visitor accepted cookies (fbq is defined only after consent).
        // Only value + currency (no product names) to comply with Meta's sensitive-data policy.
        if (window.fbq) window.fbq('track', 'Purchase', { value: orderValue, currency: orderCurrency });
      } else if (res.data.status === 'expired') {
        setStatus('expired');
      } else {
        setAttempts(prev => prev + 1);
      }
    } catch (err) {
      setAttempts(prev => prev + 1);
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status === 'checking' && attempts < 10) {
      const timer = setTimeout(checkStatus, attempts === 0 ? 0 : 2000);
      return () => clearTimeout(timer);
    }
  }, [checkStatus, status, attempts]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div className="glass-card p-8 md:p-12 max-w-lg w-full text-center" data-testid="success-card">
        {status === 'checking' && (
          <>
            <div className="spinner mx-auto mb-6"></div>
            <h1 className="text-2xl font-bold text-white mb-2">Weryfikujemy płatność...</h1>
            <p className="text-[#9CA3AF]">To może potrwać kilka sekund</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-[#10b981]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#10b981]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Dziękujemy za zakup! 🎉</h1>
            <p className="text-[#9CA3AF] mb-8">
              Twoje zamówienie zostało opłacone. Na podany adres e-mail wysłaliśmy link do pobrania materiałów.
            </p>
            <div className="glass-card p-4 mb-8 text-left">
              <div className="flex items-center gap-3 text-[#10b981]">
                <Mail size={20} />
                <span className="font-medium">Sprawdź swoją skrzynkę e-mail</span>
              </div>
              <p className="text-sm text-[#6B7280] mt-2 ml-8">
                Jeśli nie widzisz wiadomości, sprawdź folder SPAM.
              </p>
            </div>
            <div className="space-y-3">
              <button onClick={() => { window.location.href = '/'; }} className="btn-primary w-full">
                Wróć na stronę główną <ArrowRight size={18} />
              </button>
            </div>
          </>
        )}

        {status === 'expired' && (
          <>
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Sesja wygasła</h1>
            <p className="text-[#9CA3AF] mb-6">Płatność nie została zrealizowana w czasie.</p>
            <a href="/checkout" className="btn-primary">Spróbuj ponownie</a>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className="w-20 h-20 bg-[#10b981]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#10b981]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Płatność w trakcie przetwarzania</h1>
            <p className="text-[#9CA3AF] mb-6">
              Jeśli płatność została zrealizowana, wkrótce otrzymasz e-mail z linkiem do pobrania.
            </p>
            <button onClick={() => { window.location.href = '/'; }} className="btn-primary">Wróć na stronę główną</button>
          </>
        )}

        <p className="text-[#6B7280] text-sm mt-8">
          Masz pytania? <a href="/kontakt" className="text-[#10b981] hover:underline">Skontaktuj się z nami</a>
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;